<template>
  <div v-if="judgeScoreStatus" class="rubric-steps-container is-hidden-touch is-hidden-desktop-only" :class="{ 'is-IE': isIE11 }">
    <div>
      <h1 class="rubric-text-title">Judging Rubric</h1>
      <p class="rubric-text">To begin, please download the Excel rubric to fill out your scores. When complete, upload the updated excel rubric and click submit.</p>
    </div>
    <div class="item-wrapper">
      <div class="molecule">
        <span class="molecule-ring"
              :class="{ 'step-complete' : isStatusDownloaded }">
          <span v-if="!isStatusDownloaded" class="molecule-dot"/>
          <span v-if="isStatusDownloaded" class="molecule-checkmark"><i class="fa-solid fa-check"></i></span>
        </span>
        <div class="molecule-line" :class="{ 'step-complete' : isStatusUploaded }"/>
      </div>
      <div class="molecule-button" :class="{ 'step-active' : !isStatusSubmitted }" @click="downloadRubric()">DOWNLOAD RUBRIC</div>
    </div>
    <div class="item-wrapper">
      <div class="molecule">
        <span class="molecule-ring"
              :class="{ 'step-complete' : isStatusUploaded }">
          <span v-if="!isStatusUploaded" class="molecule-dot"/>
          <span v-if="isStatusUploaded" class="molecule-checkmark"><i class="fa-solid fa-check"></i></span>
        </span>
        <div class="molecule-line" :class="{ 'longer' : isStatusUploaded, 'step-complete' : isStatusUploaded }"/>
      </div>
      <div>
        <div class="molecule-button" :class="{ 'step-active' : isStatusDownloaded && !isStatusSubmitted }" @click="openUploadRubricModal()">UPLOAD RUBRIC</div>
        <p v-if="isStatusUploaded" class="judge-score">Your Score {{ judgePairingScore }}/40</p>
      </div>
    </div>
    <div class="item-wrapper">
      <div class="molecule">
        <span class="molecule-ring"
              :class="{ 'step-complete' : isStatusSubmitted }">
          <span v-if="!isStatusSubmitted" class="molecule-dot"/>
          <span v-if="isStatusSubmitted" class="molecule-checkmark"><i class="fa-solid fa-check"></i></span>
        </span>
      </div>
      <div :class="{ 'score-submitted' : isStatusSubmitted,
           'step-active' : isStatusUploaded && !isStatusSubmitted }"
           class="molecule-button"
           @click="showSubmitModal()">{{ submitScoreText }}</div>
    </div>
    <modal-rubric-upload :projectID="projectID"></modal-rubric-upload>
    <modal-confirm-submit ref="confirmSubmitModal"
             :title="'Confirm Submit'"
             :text="`<div>Are you sure you would you like to submit the score of <strong>${judgePairingScore}/40</strong> for this project?<br><strong>Submitted scores are final.</strong></div>`"
             :yesText="'Submit Score'"
             :noText="$t('oas.judging.approveModal.cancelText')"
             @yes="submitRubricScore()">
    </modal-confirm-submit>

  </div>
</template>

<script>
import userAgent from '@/libs/user-agent'
import ModalRubricUpload from '@/components/Modals/RubricUpload'
import ModalConfirmSubmit from '@/components/Modals/Confirm'

export default {
  props: ['projectID'],
  components: {
    ModalRubricUpload,
    ModalConfirmSubmit
  },
  data: function () {
    return {
      showAttachModal: false
    }
  },
  mounted: function () {
    this.$store.dispatch('getJudgePairings', { projectID: this.projectID })
  },
  methods: {
    openUploadRubricModal: function () {
      if (this.isStatusDownloaded && !this.isStatusSubmitted) {
        this.$store.dispatch('showRubricAttachModal')
      }
    },
    downloadRubric: function () {
      if (!this.isStatusSubmitted) {
        this.$store.dispatch('downloadJudgingRubric', { projectID: this.projectID }).then((response) => {
          // Create temporary anchor tag element, then set the Rubric Blob URL as the href
          const rubricElement = document.createElement('a')
          const rubricBlob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const rubricObjectUrl = URL.createObjectURL(rubricBlob)
          // Pull filename from response header
          const headerLine = response.headers['content-disposition']
          const startFileNameIndex = headerLine.indexOf('"') + 1
          const endFileNameIndex = headerLine.lastIndexOf('"')
          // Set filename for download
          rubricElement.download = headerLine.substring(startFileNameIndex, endFileNameIndex)
          rubricElement.href = rubricObjectUrl
          document.body.appendChild(rubricElement)
          rubricElement.click()
          document.body.removeChild(rubricElement)
        })
      }
    },
    showSubmitModal: function () {
      if (this.isStatusUploaded && !this.isStatusSubmitted) {
        this.$refs.confirmSubmitModal.show()
      }
    },
    submitRubricScore: function () {
      if (this.isStatusUploaded && !this.isStatusSubmitted) {
        this.$store.dispatch('submitRubricScore', { projectID: this.projectID }).then(() => {
          this.$store.dispatch('getJudgePairings', { projectID: this.projectID })
        })
      }
    }
  },
  computed: {
    isIE11: function () {
      return userAgent.isIE11()
    },
    judgePairingScore: function () {
      return this.$store.getters.getJudgePairingScore || 0
    },
    judgeScoreStatus: function () {
      return this.$store.getters.getJudgePairingStatus
    },
    isStatusDownloaded: function () {
      return ['DOWNLOADED', 'UPLOADED', 'SUBMITTED'].includes(this.judgeScoreStatus)
    },
    isStatusUploaded: function () {
      return ['UPLOADED', 'SUBMITTED'].includes(this.judgeScoreStatus)
    },
    isStatusSubmitted: function () {
      return this.judgeScoreStatus === 'SUBMITTED'
    },
    submitScoreText: function () {
      return this.isStatusSubmitted ? 'SCORE SUBMITTED' : 'SUBMIT SCORE'
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

.rubric-steps-container {
  width: 215px;
  margin: 0 0 1rem 1rem;
  background: white;
  padding: 0.5rem 1.2rem 1.1rem 1.2rem;
}

.rubric-text {
  font-size: 0.75rem;
  padding: 0 0 1rem 0;
  &-title {
    font-size: 1.25rem;
    font-weight: 600;
  }
}

.is-IE {
  right: calc(100vw/2 - 400px - 200px - 16px);
}

.item-wrapper {
  display: flex;
  flex-direction: row;
  align-items: baseline;
}

.judge-score {
  text-align: center;
  margin: 0.5rem 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #28BC00;
}

.molecule {
  &-wrapper {
    display: flex;
    flex-direction: row;
  }
  display: flex;
  flex-direction: column;
  align-items: center;
  &-ring {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    border: solid 2px grey;
    display: flex;
    justify-content: center;
    align-items: center;
    &.step-complete {
      border: solid 2px #28BC00;
      background: #28BC00;
    }
  }
  &-dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: #c0c0c0;
    &.step-complete {
      background: #28BC00;
    }
  }
  &-checkmark {
    color: white;
  }
  &-line {
    width: 2px;
    height: 1rem;
    background: grey;
    margin-top: 0.6rem;
    &.longer {
      height: 2rem;
    }
    &.step-complete {
      background: #28BC00;
    }
  }
  &-button {
    border-radius: 2px;
    cursor: default;
    margin-left: 0.75rem;
    padding: 0.7rem 0;
    background: #D1D1D1;
    width: 8.75rem;
    color: white;
    text-align: center;
    font-size: 0.75rem;
    font-weight: 600;
    &.score-submitted {
      cursor: default;
      background: #28BC00;
    }
    &.step-active {
      cursor: pointer;
      background: #005BAB;
      &:hover {
        background: #004078;
      }
    }
  }
}

</style>
